import React, { useEffect, useState } from 'react';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/firebase';
import { logger } from '@/utils/logger';
import { applyInternalTrafficForUser, applyInternalTrafficFromQuery, initInternalTrafficFromFlag } from '@/utils/internalTraffic';
import { initRedditPixel } from '@/utils/redditPixel';
import { initMetaPixel } from '@/utils/metaPixel';
import { initClarity } from '@/utils/clarity';
import { setGaUserIdentity } from '@/utils/Analytics';
import { backendAnalyticsListener } from '@/services/BackendAnalyticsListener';

/** Owned by AuthProvider; read it through `useAuth`, not directly. */
export interface FirebaseAuthState {
  currentUser: FirebaseUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

// Mock auth interface for e2e testing
interface MockFirebaseAuth {
  currentUser: FirebaseUser | null;
  onAuthStateChanged: (callback: (user: FirebaseUser | null) => void) => () => void;
}

export const useFirebaseAuthState = (): FirebaseAuthState => {
  const [authState, setAuthState] = useState(() => ({
    currentUser: null as FirebaseUser | null,
    isLoading: true
  }));

  useEffect(() => {
    // Re-tag a known-internal device as early as possible (before auth resolves).
    initInternalTrafficFromFlag();
    // ALSO honor ?internal=1 at boot: page-level effects (TryAQuestion) apply it
    // too, but they run AFTER this effect — so a FRESH device's very first
    // ?internal=1 visit used to load the pixels/Clarity before the flag landed
    // (caught by the Clarity gating verifier, 2026-08-12).
    applyInternalTrafficFromQuery();

    // Reddit Pixel AFTER the internal flag is applied, so flagged devices never load it.
    initRedditPixel();

    // Meta Pixel — same rule (inert unless VITE_META_PIXEL_ID is set).
    initMetaPixel();

    // Clarity session replay — same rule (real visitors only, never prerender/internal).
    initClarity();

    // Check for mock auth in browser context (e2e tests)
    const mockAuth = (window as Window & { __mockFirebaseAuth?: MockFirebaseAuth }).__mockFirebaseAuth;

    if (mockAuth) {
      logger.info('useFirebaseAuthState', 'Using mock Firebase auth for testing');
      const unsubscribe = mockAuth.onAuthStateChanged((user: FirebaseUser | null) => {
        logger.info('useFirebaseAuthState', 'Mock auth state changed');
        setAuthState({
          currentUser: user,
          isLoading: false
        });
      });
      return unsubscribe;
    }

    // Use real Firebase auth
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      logger.info('useFirebaseAuthState', 'Auth state changed');

      // Keep internal/test accounts out of GA4 "real users" (no-op in dev/preview
      // where GA isn't loaded).
      applyInternalTrafficForUser(user?.email);

      // Set GA4 user_id + `logged_in` user property so every metric (incl.
      // engagement/"logged-in time") can be segmented logged-in vs anonymous.
      setGaUserIdentity(user?.uid ?? null);

      // Pre-warm the core callables the moment we know who the user is — so the
      // dashboard's getUserProfile and the quiz callables land on already-warm
      // instances instead of paying a 3-12s gen2 cold start. Fire-and-forget;
      // deduped to once per page load inside warmQuizFunctions().
      if (user) {
        import('@/services/QuizApiService')
          .then(({ QuizApiService }) => QuizApiService.getInstance().warmQuizFunctions())
          .catch(() => { /* best-effort */ });
      }

      // Backend→GA4 bridge. The webhook writes `analyticsNotifications` docs the
      // browser has to forward; without this start the docs piled up unread and
      // the events (payment failures) never reached GA4. Symmetric with the
      // stopListening() in useAuthActions.logOut — this also covers the "session
      // restored on reload" case, where logOut never runs. Idempotent: a repeat
      // callback for the same uid is a no-op inside the listener.
      if (user) {
        backendAnalyticsListener.startListening(user.uid);
      } else {
        backendAnalyticsListener.stopListening();
      }

      setAuthState({
        currentUser: user,
        isLoading: false
      });
    });

    return () => {
      unsubscribe();
      backendAnalyticsListener.stopListening();
    };
  }, []);

  return React.useMemo(() => ({
    currentUser: authState.currentUser,
    isLoading: authState.isLoading,
    isAuthenticated: !!authState.currentUser
  }), [authState.currentUser, authState.isLoading]);
};
